import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthContext } from "@/context/AuthContext";
import { useModal } from "@/context/ModalContext";
import getLink, { LINK_TYPE } from "@/lib/links";
import { patchUser } from "@/services/auth";
import { fireTagManagerEvent } from "@/services/tagmanager";
import { getUrlParameter } from "@/utils/generic";
import ModalCompleteRegistration from "./ModalCompleteRegistration";
import { phoneCodes } from "./PhoneCodes";
import { SelectedHotel } from "./SelectHotelInput";
import { useSelector } from "react-redux";
import { selectUser } from "@/store/userSlice";

export function RegisterDetails() {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showModal } = useModal();

  const formSchema = z.object({
    firstName: z.string().min(1, t("account.first_name_placeholder")),
    lastName: z.string().min(1, t("account.last_name_placeholder")),
    phonePrefix: z.string(),
    phoneNumber: z.string().min(5, t("auth.valid_phone_number")),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: t("auth.must_accept_terms"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.secondName ?? "",
      phonePrefix: user?.phonePrefix ?? "+39",
      phoneNumber: user?.phone ?? "",
      acceptTerms: false,
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        firstName: user?.firstName ?? "",
        lastName: user?.secondName ?? "",
        phonePrefix: user?.phonePrefix ?? "+39",
        phoneNumber: user?.phone ?? "",
        acceptTerms: false,
      });
    }
  }, [user]);

  const { userEmail } = useAuthContext();

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    try {
      // Update user profile with additional information
      await patchUser({
        firstName: values.firstName,
        secondName: values.lastName,
        phonePrefix: values.phonePrefix,
        phone: values.phoneNumber,
      });
      let hotelName = "";
      if (SelectedHotel.value.dest_name === "") {
        const storedHotel =
          typeof window !== "undefined"
            ? sessionStorage.getItem("selectedHotel")
            : null;
        if (storedHotel) {
          const parsedData = JSON.parse(storedHotel);
          hotelName = parsedData.dest_name;
        }
      } else {
        hotelName = SelectedHotel.value.dest_name;
      }

      fireTagManagerEvent("complete_profile", {
        email: userEmail,
        collection_name: hotelName,
      });
      showModal(<ModalCompleteRegistration />, {
        title: " ",
        description: " ",
      });
      navigate(getLink(LINK_TYPE.HOME));
    } catch (error: any) {
      console.log(error);
    }
    setIsLoading(false);
  }

  return (
    <div className="mx-auto max-w-sm w-full">
      <p className="text-2xl font-medium">
        {t("auth.complete_register_title")}
      </p>
      <p className="text-base font-medium text-gray-500">
        {t("auth.complete_register_subtitle")}
      </p>
      <div className="mt-10">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex flex-col gap-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("account.first_name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("account.first_name_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("account.last_name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("account.last_name_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="phonePrefix"
                render={({ field }) => (
                  <FormItem className="w-[100px]">
                    <FormLabel>{t("account.phone_prefix")}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {phoneCodes.map(({ country, code }, index) => (
                          <SelectItem key={index} value={code}>
                            {country} {code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>{t("auth.phone")}</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder={t("auth.phone_number_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <div className="mb-12">
                <FormField
                  control={form.control}
                  name="acceptTerms"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <p className="text-xs font-medium">
                          {t("auth.accept_terms")}{" "}
                          <a
                            href="/tos"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline cursor-pointer"
                          >
                            {t("auth.terms_of_service")}
                          </a>{" "}
                          {t("auth.accept_terms_and")}{" "}
                          <a
                            href="/privacy"
                            target="_blank"
                            className="underline cursor-pointer"
                            rel="noopener"
                          >
                            {t("auth.privacy_policy")}
                          </a>
                        </p>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end items-center">
              <Button
                className="w-full"
                type="submit"
                variant={"takyon"}
                disabled={
                  isLoading ||
                  !form.formState.isValid ||
                  !form.watch("acceptTerms")
                }
              >
                {isLoading ? t("auth.registering") : t("auth.go_to_dashboard")}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
